import { computed } from '@preact/signals';
import { useLayoutEffect, useState } from 'preact/hooks';
import waitForTimers from 'wait-for-timers';
import { loadNewEpisode, pause, play, reset } from '../../global-state/audio-player';
import { getCurrentEpisode, state } from '../../global-state/audio-signals';
import { useUserDataStore } from '../../global-state/user-data';
import { Episode } from '../../models/podcasts';
import { checkIfPlayed } from '../../utils/check-if-played';
import { iconBlue } from '../../utils/colors';
import { readableDuration } from '../../utils/format-date';
import { trackPlayNewEpisodeClick } from '../../utils/plausible-events';
import { Icon } from '../icon';
import ProgressCircle from '../progress-circle';

const isLoadingSignal = computed(() => state.value === 'loading');
const isPlayingSignal = computed(() => state.value === 'playing');

const PlayButton = ({ episode, onPlay }: { episode: Episode; onPlay?: () => void }) => {
  const episodeInPlayer = getCurrentEpisode();
  const isLoading = isLoadingSignal.value;
  const isPlaying = isPlayingSignal.value;
  const isInPlayer =
    episodeInPlayer &&
    episodeInPlayer.podcastId === episode.podcastId &&
    episodeInPlayer.guidHash === episode.guidHash;
  const [isLongWait, setLongWait] = useState(false);
  const progress = useUserDataStore((s) => s.episodesProgress[episode.id]) || [];
  const [secondsPlayed, secondsLeft] = progress || [];

  useLayoutEffect(() => {
    if (isLoading) {
      return waitForTimers([200, 'raf'], () => setLongWait(true));
    } else if (isLongWait) {
      setLongWait(false);
    }
  }, [state]);

  const showLoadingIcon = isInPlayer && isLoading && isLongWait;
  const showPauseIcon = isInPlayer && isPlaying;
  const isPlayed =
    progress && progress.length ? checkIfPlayed(episode, progress) : false;
  let buttonText = !showLoadingIcon && !showPauseIcon ? 'Play' : 'Pause';
  if (isPlayed) {
    buttonText = 'Played';
  } else if (secondsPlayed > 0 && secondsLeft > 0) {
    buttonText = `${readableDuration(secondsLeft, '')} left`;
  } else if (episode.duration) {
    buttonText = `${readableDuration(episode.duration, '')}`;
  }

  let percentPlayed = secondsPlayed / (secondsPlayed + secondsLeft) || 0;
  if (percentPlayed > 0) {
    percentPlayed = Math.max(0.03, percentPlayed); // Always show at least a tiny bit of progress
  }

  return (
    <button
      class="button button--play"
      onClick={() => {
        if (!isInPlayer) {
          loadNewEpisode(episode, isPlayed ? 0 : secondsPlayed);
          play();
          trackPlayNewEpisodeClick(episode);
          onPlay?.();
        } else if (isLoading) {
          reset();
        } else if (isPlaying) {
          pause();
        } else {
          play();
          onPlay?.();
        }
      }}
    >
      <ProgressCircle
        percent={percentPlayed}
        size={'1.5rem'}
        borderWidth={'2px'}
        color={iconBlue}
        shouldEase={true}
      />
      {showPauseIcon ? (
        <Icon
          style={{
            position: 'absolute',
            left: '9px',
            top: '8px',
            height: '14px',
            width: '14px',
          }}
          sprite="pause"
        />
      ) : showLoadingIcon ? (
        <Icon
          style={{
            position: 'absolute',
            left: '9px',
            top: '8px',
            height: '14px',
            width: '14px',
            animation: 'spin 1.15s infinite',
          }}
          sprite="loading-circle"
        />
      ) : (
        <Icon
          style={{
            position: 'absolute',
            left: '10px',
            top: '8px',
            height: '14px',
            width: '14px',
          }}
          sprite="play"
        />
      )}
      {buttonText}
    </button>
  );
};

export default PlayButton;
